import { useEffect, lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import { LanguageProvider } from '@/lib/LanguageContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import ScrollToTop from './components/ScrollToTop';
import ConversionTracker from '@/components/ConversionTracker';
import CookieNotice from '@/components/smartpos/CookieNotice';
import LanguageRouterGuard from '@/components/LanguageRouterGuard';
import ContactPathGuard from '@/components/ContactPathGuard';
import { LANG_CODES, LANG_PREFIXES } from '@/lib/langPath';
const Home = lazy(() => import('./pages/Home'));
const CasosExito = lazy(() => import('./pages/CasosExito'));
const Legal = lazy(() => import('./pages/Legal'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const GuiaTPV = lazy(() => import('./pages/GuiaTPV'));
const SoftwareTPV = lazy(() => import('./pages/soluciones/SoftwareTPV'));
const CashDro = lazy(() => import('./pages/soluciones/CashDro'));
const Quioscos = lazy(() => import('./pages/soluciones/Quioscos'));
const InteligenciaArtificial = lazy(() => import('./pages/soluciones/InteligenciaArtificial'));
const HardwareTPV = lazy(() => import('./pages/soluciones/HardwareTPV'));
const CanalesVenta = lazy(() => import('./pages/soluciones/CanalesVenta'));
const MediosPago = lazy(() => import('./pages/soluciones/MediosPago'));
const Verifactu = lazy(() => import('./pages/soluciones/Verifactu'));
const Hosteleria = lazy(() => import('./pages/tipos/Hosteleria'));
const Retail = lazy(() => import('./pages/tipos/Retail'));
const Alimentacion = lazy(() => import('./pages/tipos/Alimentacion'));
const Belleza = lazy(() => import('./pages/tipos/Belleza'));
const CadenasFranquicias = lazy(() => import('./pages/tipos/CadenasFranquicias'));
const Barcelona = lazy(() => import('./pages/locales/Barcelona'));
const Madrid = lazy(() => import('./pages/locales/Madrid'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogArticle = lazy(() => import('./pages/BlogArticle'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Demo = lazy(() => import('./pages/Demo'));
const Precios = lazy(() => import('./pages/Precios'));
const Tecnicos = lazy(() => import('./pages/Tecnicos'));

// Páginas con versión propia en los cuatro idiomas: el español se sirve en la
// raíz del dominio y el resto de idiomas con su prefijo (/ca, /en, /fr), de
// modo que cada versión tiene una dirección propia e indexable.
const PAGE_LIST = [
  { path: '/', Comp: Home },
  { path: '/casos-exito', Comp: CasosExito },
  { path: '/sobre-nosotros', Comp: About },
  { path: '/contacto', Comp: Contact },
  { path: '/guia-tpv', Comp: GuiaTPV },
  { path: '/tpv-nube', Comp: SoftwareTPV },
  { path: '/cashdro', Comp: CashDro },
  { path: '/quioscos-autoservicio', Comp: Quioscos },
  { path: '/inteligencia-artificial', Comp: InteligenciaArtificial },
  { path: '/hardware-tpv', Comp: HardwareTPV },
  { path: '/canales-venta', Comp: CanalesVenta },
  { path: '/medios-pago', Comp: MediosPago },
  { path: '/verifactu', Comp: Verifactu },
  { path: '/demo', Comp: Demo },
  { path: '/precios', Comp: Precios },
  { path: '/tecnicos', Comp: Tecnicos },
  { path: '/hosteleria', Comp: Hosteleria },
  { path: '/retail', Comp: Retail },
  { path: '/alimentacion', Comp: Alimentacion },
  { path: '/belleza', Comp: Belleza },
  { path: '/cadenas-franquicias', Comp: CadenasFranquicias },
  { path: '/tpv-barcelona', Comp: Barcelona },
  { path: '/tpv-madrid', Comp: Madrid },
  { path: '/blog', Comp: Blog },
  { path: '/blog/:slug', Comp: BlogArticle },
  { path: '/legal/:section', Comp: Legal },
];

// Direcciones antiguas o cortas que siguen recibiendo visitas.
const ALIASES = [
  { path: '/quienes-somos', to: '/sobre-nosotros' },
  { path: '/about', to: '/sobre-nosotros' },
  { path: '/about-us', to: '/sobre-nosotros' },
  { path: '/complementos', to: '/precios#upgrades' },

  // Direcciones del sitio anterior: siguen apareciendo en buscadores y enlaces
  // guardados, así que llevan a la página equivalente actual en vez de a un 404.
  { path: '/contacta', to: '/contacto' },
  { path: '/peticion-de-soporte', to: '/contacto' },
  { path: '/restaurantes', to: '/hosteleria' },
  { path: '/fastfood', to: '/hosteleria' },
  { path: '/bares-cafeterias', to: '/hosteleria' },
  { path: '/heladerias', to: '/hosteleria' },
  { path: '/discotecas-pubs', to: '/hosteleria' },
  { path: '/comercios', to: '/retail' },
  { path: '/supermercados', to: '/retail' },
  { path: '/tiendas-de-moda', to: '/retail' },
  { path: '/panaderias-y-pastelerias', to: '/alimentacion' },
  { path: '/spa', to: '/belleza' },
  { path: '/self-checkout', to: '/quioscos-autoservicio' },
  { path: '/pago-con-tarjeta-y-efectivo', to: '/quioscos-autoservicio' },
  { path: '/barrandillas', to: '/quioscos-autoservicio' },
  { path: '/hiobot', to: '/inteligencia-artificial' },
  { path: '/ts10', to: '/hardware-tpv' },
  { path: '/ts20', to: '/hardware-tpv' },
  { path: '/cashdro-s', to: '/cashdro' },
  { path: '/documentacion', to: '/guia-tpv' },
  { path: '/registrarse', to: '/demo' },
  { path: '/es', to: '/' },
  { path: '/aviso-legal', to: '/legal/aviso-legal' },
  { path: '/terminos-y-condiciones', to: '/legal/aviso-legal' },
  { path: '/politica-de-privacidad', to: '/legal/privacidad' },
  { path: '/politica-de-cookies', to: '/legal/cookies' },
];

const PAGE_ROUTES = LANG_CODES.flatMap((code) => {
  const prefix = LANG_PREFIXES[code];
  return [
    ...PAGE_LIST.map(({ path, Comp }) => (
      <Route key={`${code}${path}`} path={path === '/' ? prefix || '/' : prefix + path} element={path === '/contacto' ? <ContactPathGuard><Comp /></ContactPathGuard> : <Comp />} />
    )),
    ...ALIASES.map(({ path, to }) => (
      <Route key={`${code}${path}`} path={prefix + path} element={<Navigate to={prefix + to} replace />} />
    )),
  ];
});

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin, user } = useAuth();

  // SIGNUP conversion — fires once per freshly-created user
  useEffect(() => {
    if (user && sessionStorage.getItem('gads_signup_pending') === 'true') {
      sessionStorage.removeItem('gads_signup_pending');
      const key = 'gads_signup_' + user.id;
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, '1');
        if (window.gtag) {
          window.gtag('event', 'conversion', {
            send_to: 'AW-18410505170/jCebCKzu6uccENKH6MpE',
            transaction_id: user.id,
          });
        }
      }
    }
  }, [user]);

  // Retira el shell estático de index.html (fondo del hero + logo) en cuanto la app
  // pinta su propio header, para que no se solapen.
  useEffect(() => {
    if (!isLoadingPublicSettings && !isLoadingAuth) {
      document.getElementById('boot-shell')?.remove();
    }
  }, [isLoadingPublicSettings, isLoadingAuth]);

  // Show loading spinner while checking app public settings or auth
  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="boot-loading fixed inset-0 flex items-center justify-center">
        <div className="boot-spinner w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Handle authentication errors
  if (authError && authError.type === 'user_not_registered') {
    return <UserNotRegisteredError />;
  }

  // Render the main app
  return (
    <Suspense fallback={<div className="boot-loading fixed inset-0 flex items-center justify-center"><div className="boot-spinner w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div></div>}>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {PAGE_ROUTES}
      <Route path="*" element={<PageNotFound />} />
    </Routes>
    </Suspense>
  );
};


function App() {
  // gtag relay (para Base44 builder preview) + PHONE_CALL_LEAD conversion
  // gtag.js se carga en index.html junto a GTM para evitar doble etiquetado
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const inIframe = (() => { try { return window.self !== window.top; } catch { return true; } })();
    const originalGtag = window.gtag;
    window.gtag = function gtag() {
      if (originalGtag) originalGtag.apply(null, arguments);
      if (inIframe) {
        try {
          const args = Array.prototype.slice.call(arguments);
          const cmd = args[0];
          window.parent.postMessage({
            type: 'base44_gtag_event',
            event: {
              source: 'gtag',
              timestamp: new Date().toLocaleTimeString(),
              command: cmd,
              params: args.slice(1),
              type: cmd === 'event' ? (args[1] || 'event') : cmd,
            },
          }, window.location.origin);
        } catch (_e) { /* relay must not break gtag */ }
      }
    };

    // PHONE_CALL_LEAD — fire on any tel: link click
    const handlePhoneClick = (e) => {
      const link = e.target.closest('a[href^="tel:"]');
      if (link && window.gtag) {
        window.gtag('event', 'conversion', {
          send_to: 'AW-18410505170/yTL9CNnP6-ccENKH6MpE',
        });
      }
    };
    document.addEventListener('click', handlePhoneClick);
    return () => document.removeEventListener('click', handlePhoneClick);
  }, []);

  return (
    <AuthProvider>
      <QueryClientProvider client={queryClientInstance}>
        <Router>
          <LanguageProvider>
            <ScrollToTop />
            <ConversionTracker />
            <LanguageRouterGuard />
            <AuthenticatedApp />
            <CookieNotice />
          </LanguageProvider>
        </Router>
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App